/**
 * Team Management - Settings Page
 *
 * Manage organization team members who can be assigned to governance roles.
 * These employees become available for persona assignment in the FAIR
 * Personas topology view (DC-1648), and each member can carry a direct
 * FAIR-persona assignment via the dropdown here (org_employees.persona_id).
 *
 * @version 2.0.0
 * @date 2025-11-30
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { useAuth } from '../../hooks/useAuth';
import './team-management.css';

// =============================================================================
// TYPES
// =============================================================================

export interface Employee {
  employee_id: string;
  display_name: string;
  email: string;
  department?: string;
  job_title?: string;
  // DC-1648: optional FAIR-persona assignment (FK personas.id, SET NULL on
  // persona delete). persona_name is server-side LEFT JOIN enrichment.
  persona_id?: string | null;
  persona_name?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

/** Dropdown option shape from GET /api/personas (canonical Persona, trimmed). */
interface PersonaOption {
  id: string;
  name: string;
  archetype: string | null;
}

interface EmployeeFormData {
  display_name: string;
  email: string;
  department: string;
  job_title: string;
  /** '' = no persona assigned (sent as null). */
  persona_id: string;
}

const INITIAL_FORM_DATA: EmployeeFormData = {
  display_name: '',
  email: '',
  department: '',
  job_title: '',
  persona_id: '',
};

// =============================================================================
// EMPLOYEE CARD COMPONENT
// =============================================================================

interface EmployeeCardProps {
  employee: Employee;
  onEdit: (employee: Employee) => void;
  onToggleActive: (employee: Employee) => void;
  onDelete: (employee: Employee) => void;
}

const EmployeeCard: React.FC<EmployeeCardProps> = ({
  employee,
  onEdit,
  onToggleActive,
  onDelete,
}) => {
  const initials = employee.display_name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className={`employee-card ${!employee.is_active ? 'inactive' : ''}`}>
      <div className="employee-avatar">{initials}</div>
      <div className="employee-info">
        <h3 className="employee-name">{employee.display_name}</h3>
        <p className="employee-email">{employee.email}</p>
        {(employee.job_title || employee.department) && (
          <p className="employee-role">
            {employee.job_title}
            {employee.job_title && employee.department && ' • '}
            {employee.department}
          </p>
        )}
        {employee.persona_name && (
          <span
            className="employee-persona-chip"
            data-testid={`employee-persona-${employee.employee_id}`}
            title="Assigned FAIR persona"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              marginTop: '0.25rem',
              padding: '0.125rem 0.5rem',
              borderRadius: 999,
              background: 'var(--tri-teal-tint)',
              border: '1px solid var(--tri-teal-tint-line)',
              color: 'var(--tri-teal)',
              fontSize: '0.6875rem',
              fontWeight: 600,
            }}
          >
            {employee.persona_name}
          </span>
        )}
      </div>
      <div className="employee-status">
        <span className={`status-badge ${employee.is_active ? 'active' : 'inactive'}`}>
          {employee.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>
      <div className="employee-actions">
        <button
          className="btn btn-icon"
          onClick={() => onEdit(employee)}
          title="Edit"
        >
          ✏️
        </button>
        <button
          className="btn btn-icon"
          onClick={() => onToggleActive(employee)}
          title={employee.is_active ? 'Deactivate' : 'Activate'}
        >
          {employee.is_active ? '🚫' : '✅'}
        </button>
        <button
          className="btn btn-icon btn-danger"
          onClick={() => onDelete(employee)}
          title="Delete"
        >
          🗑️
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// ADD/EDIT MODAL COMPONENT
// =============================================================================

interface EmployeeModalProps {
  isOpen: boolean;
  employee: Employee | null; // null for add, Employee for edit
  /** DC-1648: FAIR persona options for the assignment dropdown. */
  personas: PersonaOption[];
  onSave: (data: EmployeeFormData) => Promise<void>;
  onClose: () => void;
}

const EmployeeModal: React.FC<EmployeeModalProps> = ({
  isOpen,
  employee,
  personas,
  onSave,
  onClose,
}) => {
  const [formData, setFormData] = useState<EmployeeFormData>(INITIAL_FORM_DATA);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = employee !== null;

  useEffect(() => {
    if (employee) {
      setFormData({
        display_name: employee.display_name,
        email: employee.email,
        department: employee.department || '',
        job_title: employee.job_title || '',
        persona_id: employee.persona_id || '',
      });
    } else {
      setFormData(INITIAL_FORM_DATA);
    }
    setError(null);
  }, [employee, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.display_name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save employee');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Team Member' : 'Add Team Member'}</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="form-error">{error}</div>}

            <div className="form-group">
              <label htmlFor="display_name">
                Full Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="display_name"
                value={formData.display_name}
                onChange={(e) =>
                  setFormData({ ...formData, display_name: e.target.value })
                }
                placeholder="John Smith"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Email <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="john.smith@company.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="job_title">Job Title</label>
              <input
                type="text"
                id="job_title"
                value={formData.job_title}
                onChange={(e) =>
                  setFormData({ ...formData, job_title: e.target.value })
                }
                placeholder="Data Scientist"
              />
            </div>

            <div className="form-group">
              <label htmlFor="department">Department</label>
              <input
                type="text"
                id="department"
                value={formData.department}
                onChange={(e) =>
                  setFormData({ ...formData, department: e.target.value })
                }
                placeholder="Research & Development"
              />
            </div>

            {/* DC-1648: FAIR-persona assignment */}
            <div className="form-group">
              <label htmlFor="persona_id">FAIR Persona</label>
              <select
                id="persona_id"
                data-testid="employee-persona-select"
                value={formData.persona_id}
                onChange={(e) =>
                  setFormData({ ...formData, persona_id: e.target.value })
                }
                disabled={personas.length === 0}
              >
                <option value="">— No persona —</option>
                {personas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.archetype ? ` (${p.archetype})` : ''}
                  </option>
                ))}
              </select>
              {personas.length === 0 && (
                <p className="form-hint" style={{ fontSize: '0.75rem', margin: '0.25rem 0 0 0' }}>
                  No FAIR personas seeded yet — seed them in the FAIR Personas tab first.
                </p>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : isEdit ? 'Update' : 'Add Team Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const TeamManagement: React.FC = () => {
  const { authFetch } = useApiClient();
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');

  // State
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [personas, setPersonas] = useState<PersonaOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  // Fetch employees
  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authFetch('/api/employees');

      if (!response.ok) {
        throw new Error(`Failed to fetch employees: ${response.statusText}`);
      }

      const data = await response.json();
      setEmployees(data.employees || []);
    } catch (err: unknown) {
      console.error('[TeamManagement] Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch employees');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable from useApiClient
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // DC-1648: FAIR persona options for the assignment dropdown.
  useEffect(() => {
    const fetchPersonas = async () => {
      try {
        const response = await authFetch('/api/personas');
        if (response.ok) {
          const data = (await response.json()) as PersonaOption[];
          setPersonas(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('[TeamManagement] Failed to fetch personas:', err);
      }
    };
    fetchPersonas();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable from useApiClient
  }, []);

  // Handlers
  const handleAddEmployee = () => {
    setEditingEmployee(null);
    setShowModal(true);
  };

  const handleEditEmployee = (employee: Employee) => {
    setEditingEmployee(employee);
    setShowModal(true);
  };

  const handleSaveEmployee = async (data: EmployeeFormData) => {
    const isEdit = editingEmployee !== null;
    const url = isEdit
      ? `/api/employees/${editingEmployee.employee_id}`
      : '/api/employees';
    const method = isEdit ? 'PUT' : 'POST';

    const response = await authFetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      // DC-1648: '' (no selection) clears the persona assignment (null).
      body: JSON.stringify({ ...data, persona_id: data.persona_id || null }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to save employee');
    }

    fetchEmployees();
  };

  const handleToggleActive = async (employee: Employee) => {
    try {
      const response = await authFetch(`/api/employees/${employee.employee_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: !employee.is_active }),
      });

      if (!response.ok) {
        throw new Error('Failed to update employee status');
      }

      fetchEmployees();
    } catch (err: unknown) {
      console.error('[TeamManagement] Toggle error:', err);
      alert(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const handleDeleteEmployee = async (employee: Employee) => {
    if (!confirm(`Delete ${employee.display_name}? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await authFetch(`/api/employees/${employee.employee_id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete employee');
      }

      fetchEmployees();
    } catch (err: unknown) {
      console.error('[TeamManagement] Delete error:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete employee');
    }
  };

  // Filter employees
  const filteredEmployees = employees.filter((e) => {
    // Filter by active status
    if (!showInactive && !e.is_active) return false;

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        e.display_name.toLowerCase().includes(query) ||
        e.email.toLowerCase().includes(query) ||
        e.department?.toLowerCase().includes(query) ||
        e.job_title?.toLowerCase().includes(query)
      );
    }

    return true;
  });

  // Stats
  const activeCount = employees.filter((e) => e.is_active).length;
  const inactiveCount = employees.filter((e) => !e.is_active).length;

  // =============================================================================
  // RENDER
  // =============================================================================

  // v1.2 hardening: Team Management writes are admin-only on the server
  // (employee routes now require requireAdmin per PR A). Mirror that
  // gate on the client so non-admins don't see add/edit/deactivate/delete
  // buttons that would 403 anyway. Pattern matches UserManagement.tsx.
  if (!isAdmin) {
    return (
      <div className="team-management">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You need administrator privileges to manage team members.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="team-management">
      {/* Header */}
      <div className="management-header">
        <div className="header-left">
          <h2>Team Management</h2>
          <p className="subtitle">
            Add team members who can be assigned to governance roles for your datasets
          </p>
        </div>
        <div className="header-right">
          <button className="btn btn-primary btn-lg" onClick={handleAddEmployee}>
            <span className="icon">+</span> Add Team Member
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-value">{activeCount}</span>
          <span className="stat-label">Active Members</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{inactiveCount}</span>
          <span className="stat-label">Inactive</span>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="search-section">
        <input
          type="text"
          className="search-input"
          placeholder="Search by name, email, department..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive members
        </label>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading team members...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Failed to Load Team</h3>
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={fetchEmployees}>
            Retry
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && employees.length === 0 && (
        <div className="empty-state">
          <div className="empty-illustration">👥</div>
          <h2>No Team Members Yet</h2>
          <p>
            Add your team members here so they can be assigned to governance roles
            in the FAIR Personas topology.
          </p>
          <button className="btn btn-primary btn-lg" onClick={handleAddEmployee}>
            Add Your First Team Member
          </button>
        </div>
      )}

      {/* Employee List */}
      {!loading && !error && filteredEmployees.length > 0 && (
        <div className="employee-list">
          {filteredEmployees.map((employee) => (
            <EmployeeCard
              key={employee.employee_id}
              employee={employee}
              onEdit={handleEditEmployee}
              onToggleActive={handleToggleActive}
              onDelete={handleDeleteEmployee}
            />
          ))}
        </div>
      )}

      {/* No Results State */}
      {!loading && !error && employees.length > 0 && filteredEmployees.length === 0 && (
        <div className="no-results-state">
          <p>No team members match your search: "{searchQuery}"</p>
          <button className="btn btn-secondary" onClick={() => setSearchQuery('')}>
            Clear Search
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      <EmployeeModal
        isOpen={showModal}
        employee={editingEmployee}
        personas={personas}
        onSave={handleSaveEmployee}
        onClose={() => {
          setShowModal(false);
          setEditingEmployee(null);
        }}
      />
    </div>
  );
};

export default TeamManagement;
